import { Link } from 'react-router-dom'
import { useQuery } from '@tanstack/react-query'
import { ChevronRight, Star } from 'lucide-react'
import { api } from '../../lib/api.js'
import { fmtDay } from './lib.js'
import BackBar from './BackBar.jsx'

/**
 * Social media digest (Charles, 2026-09-01): every session flagged while it
 * was logged — the public title of the book/show/film, and any notable
 * talent on it — in one list, newest first. Admin-only; the server answers
 * everyone else with an empty list, so the row into here never renders for
 * them.
 *
 * A multi-day session is ONE entry, not one per day: the fields carry
 * forward on recall, and the server unions the days' values.
 */
export default function SocialPage() {
  const { data, isLoading } = useQuery({
    queryKey: ['sl-social'],
    queryFn: () => api('/log/social'),
    refetchOnWindowFocus: true,
  })

  const entries = data?.entries || []

  return (
    <div>
      <BackBar to="/log" label="Session Log" />

      <div className="mb-4 border-b border-hairline pb-3">
        <h1 className="text-2xl font-semibold text-ink">Social media</h1>
        <p className="mt-1 text-sm text-muted">
          Sessions flagged as worth posting about. Engineers fill in the title and any
          notable talent while logging the day; everything they flagged collects here.
        </p>
      </div>

      {isLoading ? (
        <p className="text-sm text-muted">Loading…</p>
      ) : data?.admin === false ? (
        <p className="py-10 text-center text-sm text-muted">This digest is admin-only.</p>
      ) : entries.length === 0 ? (
        <p className="py-10 text-center text-sm text-muted">
          Nothing flagged yet. The Social media fields on a session feed this list.
        </p>
      ) : (
        <div className="space-y-3">
          {entries.map((e) => (
            <Entry key={e.key} e={e} />
          ))}
        </div>
      )}
    </div>
  )
}

/** One day reads "Aug 30, 2026"; a span within one year drops the repeated
 *  year — "Aug 25 – Aug 26, 2026" — so the meta line doesn't wrap twice on a
 *  phone. A locale that formats without a trailing year is left alone. */
function fmtSpan(first, last) {
  if (!first || first === last) return fmtDay(last)
  const from = first.slice(0, 4) === last.slice(0, 4) ? fmtDay(first).replace(/,?\s*\d{4}$/, '') : fmtDay(first)
  return `${from} – ${fmtDay(last)}`
}

function Entry({ e }) {
  // The flagged title is the heading; without one, the session's own name
  // stands in and doesn't get repeated in the meta line below.
  const heading = e.title || e.name
  const meta = [
    e.title && e.name !== e.title ? e.name : null,
    fmtSpan(e.firstDate, e.lastDate),
    e.days > 1 ? `${e.days} days` : null,
    e.by ? `logged by ${e.by}` : null,
  ].filter(Boolean)

  return (
    <Link to={e.path} className="card tap block p-4 active:bg-grouped">
      <div className="flex items-start gap-3">
        <Star size={17} className="mt-1 shrink-0 text-cyan" />
        <div className="min-w-0 flex-1">
          <div className="font-serif text-lg font-semibold text-ink">{heading}</div>
          {e.talent && <div className="mt-0.5 text-sm text-ink">{e.talent}</div>}
          <div className="mt-1 text-xs text-muted">{meta.join(' · ')}</div>
        </div>
        <ChevronRight size={16} className="mt-1 shrink-0 text-faint" />
      </div>

      {/* The day's photos — the raw material for the post. Its own
          horizontal scroller: the shell's pane is locked to vertical. */}
      {e.photos.length > 0 && (
        <div className="mt-3 flex gap-2 overflow-x-auto">
          {e.photos.map((p) => (
            <img
              key={p.url}
              src={p.thumb}
              alt=""
              loading="lazy"
              className="h-16 w-16 shrink-0 rounded-lg border border-hairline object-cover"
            />
          ))}
        </div>
      )}
    </Link>
  )
}
